/**
 * PDP main gallery: desktop hover zoom, click-to-fullscreen, and modal click-zoom.
 */
(() => {
  "use strict";

  const DESKTOP_MIN = 1024;
  const CAROUSEL_SELECTOR = '[id^="ProductMainImages-"]';
  const GRID_SELECTOR = '[id^="ProductGalleryGrid-"]';
  const MODAL_CAROUSEL_SELECTOR = '[id^="ProductModalCarousel-"]';
  const TAP_THRESHOLD = 10;

  const canHoverZoom = () =>
    window.innerWidth >= DESKTOP_MIN &&
    window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  const isIgnoredTarget = (target) => {
    if (!(target instanceof Element)) return true;
    return !!target.closest("button, a, video, iframe");
  };

  const getVisibleGridIndex = (gridEl, item) => {
    const wrapper = gridEl.querySelector(".product__images--grid-items");
    if (!wrapper || !item) return 0;
    const items = wrapper.querySelectorAll("[js-gallery-grid-item]");
    return Math.max(0, Array.from(items).indexOf(item));
  };

  const openFullscreenModal = (containerEl, slideIndex) => {
    const productEl = containerEl.closest("s-product");
    const trigger = productEl?.querySelector("[js-zoom-trigger]");
    if (!trigger || containerEl.dataset.zoomOpening === "true") return;

    if (typeof slideIndex === "number" && productEl) {
      productEl.dataset.pendingZoomIndex = String(slideIndex);
    }

    containerEl.dataset.zoomOpening = "true";

    const swallowClick = (event) => {
      event.stopPropagation();
    };
    document.addEventListener("click", swallowClick, true);

    window.setTimeout(() => {
      document.removeEventListener("click", swallowClick, true);
      trigger.click();
      containerEl.dataset.zoomOpening = "false";
    }, 0);
  };

  const resetAll = (containerEl) => {
    containerEl?.querySelectorAll(".js-hover-zoom.is-zooming").forEach((frame) => {
      frame.classList.remove("is-zooming");
    });
  };

  const ensureHighRes = (frame) => {
    const img = frame.querySelector("img");
    const zoomSrc = frame.getAttribute("data-zoom-src");
    if (frame.classList.contains("product__image--blend")) {
      const blendZoomSrc = frame.getAttribute("data-blend-zoom-src") || zoomSrc;
      if (blendZoomSrc) {
        frame.style.setProperty("--gallery-blend-bg-image", `url("${blendZoomSrc}")`);
      }
      if (img && zoomSrc && img.dataset.zoomLoaded !== "true") {
        img.src = zoomSrc;
        img.dataset.zoomLoaded = "true";
      }
      return;
    }
    if (!img || !zoomSrc || img.dataset.zoomLoaded === "true") return;

    img.src = zoomSrc;
    img.dataset.zoomLoaded = "true";
  };

  const updateZoom = (frame, event) => {
    const rect = frame.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    frame.style.setProperty("--zoom-x", `${((event.clientX - rect.left) / rect.width) * 100}%`);
    frame.style.setProperty("--zoom-y", `${((event.clientY - rect.top) / rect.height) * 100}%`);
    frame.classList.add("is-zooming");
  };

  const bindPointerZoom = (containerEl, getSlideIndex) => {
    let tapX = 0;
    let tapY = 0;

    containerEl.addEventListener("pointerdown", (event) => {
      tapX = event.clientX;
      tapY = event.clientY;
    });

    containerEl.addEventListener("pointerup", (event) => {
      if (Math.hypot(event.clientX - tapX, event.clientY - tapY) > TAP_THRESHOLD) return;

      const target = event.target;
      if (isIgnoredTarget(target)) return;

      const gridItem = target.closest("[js-gallery-grid-item]");
      const clickFrame = target.closest(".js-click-fullscreen");
      const hoverFrame = target.closest(".js-hover-zoom");

      if (clickFrame || (gridItem && hoverFrame)) {
        const slideIndex = getSlideIndex ? getSlideIndex(gridItem || clickFrame) : undefined;
        openFullscreenModal(containerEl, slideIndex);
        return;
      }

      if (window.innerWidth >= DESKTOP_MIN) return;
      if (!hoverFrame) return;

      openFullscreenModal(containerEl);
    });

    containerEl.addEventListener("mousemove", (event) => {
      if (!canHoverZoom()) {
        resetAll(containerEl);
        return;
      }

      const frame = event.target.closest(".js-hover-zoom");
      if (!frame) {
        resetAll(containerEl);
        return;
      }

      ensureHighRes(frame);
      updateZoom(frame, event);
    });

    containerEl.addEventListener("mouseleave", () => resetAll(containerEl));
  };

  const bindCarousel = (carouselEl) => {
    if (!carouselEl || carouselEl.dataset.hoverZoomBound === "true") return;
    carouselEl.dataset.hoverZoomBound = "true";

    bindPointerZoom(carouselEl);

    const bindSlideReset = () => {
      const swiper = carouselEl.swiper;
      if (!swiper) return false;
      if (swiper.__hoverZoomBound) return true;

      swiper.__hoverZoomBound = true;
      swiper.on("slideChange", () => resetAll(carouselEl));
      return true;
    };

    if (!bindSlideReset()) {
      const timer = window.setInterval(() => {
        if (bindSlideReset()) window.clearInterval(timer);
      }, 100);
      window.setTimeout(() => window.clearInterval(timer), 5000);
    }
  };

  const bindGrid = (gridEl) => {
    if (!gridEl || gridEl.dataset.hoverZoomBound === "true") return;
    gridEl.dataset.hoverZoomBound = "true";

    bindPointerZoom(gridEl, (item) => getVisibleGridIndex(gridEl, item));
  };

  const syncModalCarouselSwipe = (swiper, scale) => {
    const isZoomed = (scale ?? swiper.zoom?.scale ?? 1) > 1;
    swiper.allowTouchMove = !isZoomed;

    if (swiper.mousewheel) {
      if (isZoomed) {
        swiper.mousewheel.disable();
      } else {
        swiper.mousewheel.enable();
      }
    }
  };

  const bindModalCarousel = (carouselEl) => {
    if (!carouselEl || carouselEl.dataset.modalZoomBound === "true") return;

    const bind = () => {
      const swiper = carouselEl.swiper;
      if (!swiper?.zoom) return false;

      carouselEl.dataset.modalZoomBound = "true";

      let tapX = 0;
      let tapY = 0;

      carouselEl.addEventListener("pointerdown", (event) => {
        tapX = event.clientX;
        tapY = event.clientY;
      });

      carouselEl.addEventListener("pointerup", (event) => {
        if (Math.hypot(event.clientX - tapX, event.clientY - tapY) > TAP_THRESHOLD) return;
        if (isIgnoredTarget(event.target)) return;
        if (!event.target.closest(".swiper-zoom-container")) return;

        swiper.zoom.toggle(event);
      });

      swiper.on("zoomChange", (_swiper, scale) => {
        syncModalCarouselSwipe(swiper, scale);
      });

      swiper.on("slideChange", () => {
        if (swiper.zoom?.scale && swiper.zoom.scale > 1) {
          swiper.zoom.out();
        }

        syncModalCarouselSwipe(swiper);
      });

      syncModalCarouselSwipe(swiper);

      return true;
    };

    if (!bind()) {
      const timer = window.setInterval(() => {
        if (bind()) window.clearInterval(timer);
      }, 100);
      window.setTimeout(() => window.clearInterval(timer), 5000);
    }
  };

  const init = () => {
    document.querySelectorAll(CAROUSEL_SELECTOR).forEach(bindCarousel);
    document.querySelectorAll(GRID_SELECTOR).forEach(bindGrid);
    document.querySelectorAll(MODAL_CAROUSEL_SELECTOR).forEach(bindModalCarousel);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  window.addEventListener("resize", () => {
    if (!canHoverZoom()) {
      document.querySelectorAll(CAROUSEL_SELECTOR).forEach(resetAll);
      document.querySelectorAll(GRID_SELECTOR).forEach(resetAll);
    }
  });
})();
